import ks from "../kBrowserShared";

declare global { interface Window { JSZip: any; } }
import JSZip from "..//Zip/jszip.min.js";
// Node-capable (2026-10-01, kPptx): there is no `window` under Node, so the global is only published
// where one exists and every use goes through kZip.JsZipClass(). Browser behaviour is unchanged.
if (typeof window !== "undefined") { window.JSZip = JSZip; }

/**
 * The shape every handler factory below falls back to in error mode. A module-level function
 * rather than a static method on purpose: it holds no state and does nothing, so wrapping it
 * in the guard + try/catch pattern would be pure ceremony around `return;`.
 */
function ZipNoOp(): void { return; }

export class kZip {
    public static Dummy(): void {
        if (ks.KErrorMode === true) { return; }
        try {
            void kZip.JsZipClass();
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.Dummy"); }
    }

    /**
     * The JSZip class: the page's `window.JSZip` when there is one (so a host or a test that
     * replaced it is still honoured), otherwise the library this module imported - which is the
     * case under Node, where there is no `window`.
     */
    // DOCUMENTED EXCEPTION: pure lookup used by already-guarded methods; a guard here would only
    // hide the failure of the lookup itself.
    private static JsZipClass(): any {
        if (typeof window !== "undefined" && window.JSZip) { return window.JSZip; }
        return JSZip;
    }

    // Lifecycle

    /**
     * A NEW, EMPTY package, ready to be written into with WriteText / WriteArrayBuffer.
     *
     * Every other lifecycle method here LOADS an existing archive, which is all any consumer had
     * ever needed: the Office helpers in this folder read or rewrite a package somebody else made.
     * A consumer that COMPOSES one - ContractsManager's contract builder is the first - otherwise
     * has to reach past this class to `new window.JSZip()`, and a project touching JSZip directly
     * is exactly what this wrapper exists to stop: it is the one place that knows the archive
     * library's name, so it is the one place that would have to change if it ever moved.
     *
     * Answers null in error mode and on failure, never a half-built object - a caller that cannot
     * get a package must say so rather than write into nothing.
     */
    public static Empty(): any {
        if (ks.KErrorMode === true) { return null; }
        try {
            const ZipClass: any = kZip.JsZipClass();
            return new ZipClass();
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.Empty"); return null; }
    }

    /**
     * `OnError` (2026-09-18, additive - every existing caller omits it and is unchanged).
     *
     * WITHOUT IT A REFUSED LOAD IS A CHAIN THAT SIMPLY STOPS. `cb` is never called, so a caller
     * driving a callback chain off this method has no outcome, no sentence and a screen waiting for
     * ever - this estate's recorded "hang factory" shape, reached from a caller that had no
     * parameter with which to avoid it. Bytes that are not a zip are an ORDINARY user condition
     * (somebody picked the wrong file), not a programming error.
     *
     * ⚠ WHEN A HANDLER IS SUPPLIED THE FAILURE IS DELEGATED, NOT HIDDEN, and the central report is
     * skipped ON PURPOSE - `ks.KGlobalErrorHandler` FLIPS `KErrorMode`, after which every guarded
     * method in the product returns its safe default, so the caller's own explanation would never
     * render. Same contract as `kSPRest.RestExecute`'s `ErrorHandler`: supplying one means you own
     * the outcome. Omit it and the central handler still runs, exactly as before.
     */
    public static FromBlob(source: Blob | ArrayBuffer | Uint8Array, cb: (zip?: any) => void,
        OnError?: (err: any) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const p = kZip.JsZipClass().loadAsync(source);
            // ⚠ CHAINED, NOT TWO SEPARATE ATTACHMENTS. `p.then(h)` returns a NEW promise; attaching
            // `.catch` to `p` leaves that derived one unhandled, so a rejection surfaced as an
            // UNHANDLED REJECTION rather than through the handler - which is how this went
            // unnoticed. Chaining also catches a throw inside the success handler.
            p.then(kZip.FromBlob_OnSuccess(cb)).catch(kZip.FailStep("kZip.FromBlob", OnError));
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.FromBlob"); }
    }

    public static FromBase64(Base64Data: string, cb: (zip?: any) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Binary: string = atob(Base64Data);
            const Bytes: Uint8Array = new Uint8Array(Binary.length);
            for (let I: number = 0; I < Binary.length; I++) {
                Bytes[I] = Binary.charCodeAt(I);
            }
            const p = kZip.JsZipClass().loadAsync(Bytes);
            p.then(kZip.FromBlob_OnSuccess(cb)).catch(kZip.LogError("kZip.FromBase64"));
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.FromBase64"); }
    }

    public static CleanBase64(Input: string): string {
        if (ks.KErrorMode === true) { return ""; }
        try {
            let EndIdx: number = Input.length;
            while (EndIdx > 0) {
                const CharCode: number = Input.charCodeAt(EndIdx - 1);
                const IsBase64Char: boolean = (CharCode >= 65 && CharCode <= 90)
                    || (CharCode >= 97 && CharCode <= 122)
                    || (CharCode >= 48 && CharCode <= 57)
                    || CharCode === 43 || CharCode === 47 || CharCode === 61;
                if (IsBase64Char) { break; }
                EndIdx--;
            }
            if (EndIdx === Input.length) { return Input; }
            return Input.substring(0, EndIdx);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.CleanBase64");
            return Input;
        }
    }

    public static ToBlob(zip: any, cb: (blob?: Blob) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const p = zip.generateAsync({ 
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: {
                    level: 9
                }
            });
            p.then(kZip.ToBlob_OnSuccess(cb)).catch(kZip.LogError("kZip.ToBlob"));
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.ToBlob"); }
    }
    
    /**
     * The whole package as bytes (additive 2026-10-01, kPptx). `ToBlob` needs a browser `Blob`;
     * this answers a `Uint8Array`, which exists under Node as well. Same DEFLATE level as ToBlob.
     * `OnError` behaves as FromBlob's: supplying one DELEGATES the failure, omitting it reports it.
     */
    public static ToUint8Array(zip: any, cb: (bytes: Uint8Array) => void,
        OnError?: (err: any) => void, OnProgress?: (Percent: number) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // `OnProgress` (additive 2026-10-04, kDeck "Download all"): told 0-100 as the archive is generated, so a caller can show live progress.
            // JSZip passes metadata { percent, currentFile }; only the percent is forwarded.
            let Update: ((Meta: any) => void) | undefined = undefined;
            if (OnProgress !== undefined) { Update = kZip.ProgressOf(OnProgress); }
            const p = zip.generateAsync({
                type: "uint8array",
                compression: "DEFLATE",
                compressionOptions: { level: 9 }
            }, Update);
            p.then(kZip.ToUint8Array_OnSuccess(cb)).catch(kZip.FailStep("kZip.ToUint8Array", OnError));
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.ToUint8Array"); }
    }

    // Queries
    public static Exists(zip: any, path: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            return !!zip.file(path);
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.Exists"); return false; }
    }

    public static ListPaths(zip: any, prefix?: string): string[] {
        if (ks.KErrorMode === true) { return []; }
        try {
            const out: string[] = [];
            const fn = kZip.ListPaths_Collector(prefix, out);
            zip.forEach(fn);
            return out.sort();
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.ListPaths"); return []; }
    }

    // Reads
    /** `OnError` is additive and behaves exactly as FromBlob's - see that method's note. */
    public static ReadText(zip: any, path: string, cb: (text?: string) => void,
        OnError?: (err: any) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // The rejection path the shared spine wires for both readers; without it a corrupt
            // entry reached nothing at all - the shape this estate records as a "hang factory".
            kZip.ReadEntry<string>(zip, path, "text", kZip.ReadText_OnSuccess, cb, OnError, "kZip.ReadText");
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.ReadText"); }
    }

    /** `OnError` is additive and behaves exactly as FromBlob's - see that method's note. */
    public static ReadArrayBuffer(zip: any, path: string, cb: (buffer?: ArrayBuffer) => void,
        OnError?: (err: any) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            kZip.ReadEntry<ArrayBuffer>(zip, path, "arraybuffer", kZip.ReadArrayBuffer_OnSuccess, cb, OnError, "kZip.ReadArrayBuffer");
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.ReadArrayBuffer"); }
    }

    /** The zip-entry read wiring shared by kZip.ReadText and kZip.ReadArrayBuffer (finding 6817):
     * locate the entry, fail it through kZip.FailStep when missing, read it as Kind, then wire
     * the caller's success thunk and the shared FailStep rejection path.
     * DOCUMENTED EXCEPTION: pure wiring around f.async - no gate/try of its own; both callers
     * keep their KErrorMode gate, try/catch and per-member label, and kZip.FailStep keeps the
     * rejection reporting (tag = the caller's Label argument, the parameterisation FailStep
     * itself already practises). */
    private static ReadEntry = <T>(zip: any, path: string, Kind: string,
        Success: (cb: (v?: T) => void) => (v: any) => void, cb: (v?: T) => void,
        OnError: ((err: any) => void) | undefined, Label: string): void => {
        // DOCUMENTED EXCEPTION: pure wiring around f.async - no gate/try of its own (see the
        // block comment above); both callers keep their KErrorMode gate, try/catch and label.
        const f = zip.file(path);
        if (!f) { kZip.FailStep(Label, OnError)(new Error("Missing file: " + path)); return; }
        const p = f.async(Kind);
        p.then(Success(cb)).catch(kZip.FailStep(Label, OnError));
    };

    // Writes

    /**
     * `CreateFolders` (2026-09-18, additive - it defaults to JSZip's own behaviour, so every
     * existing caller writes byte-identical archives).
     *
     * ⚠ PASS `false` WHEN COMPOSING AN OPC PACKAGE - an .docx, .xlsx, .pptx or .sppkg. By default
     * JSZip adds an explicit ZIP entry for every intermediate directory, so writing
     * `word/media/image1.png` also puts `word/`, `word/media/` and friends in the archive. A
     * directory entry is not a PART, so `[Content_Types].xml` cannot describe it, and OPC requires
     * every part to be described - which is what makes Word offer to repair a document. Office's
     * own files contain no directory entries at all, and this estate's rule for OOXML is that
     * Word's tolerance is MEASURED, not reasoned: the safe output is the one Word itself writes.
     */
    public static WriteText(zip: any, path: string, text: string, Callback:any,
        CreateFolders: boolean = true): void {
        if (ks.KErrorMode === true) { return; }
        try {
            zip.file(path, text || "", { createFolders: CreateFolders });
            ks.DoCallBack(Callback);
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.WriteText"); }
    }

    /** `CreateFolders` is additive and means exactly what it does on WriteText - see that note. */
    /** `Store` (additive 2026-10-27): true writes the entry uncompressed (JSZip's per-file "STORE" overrides the archive's
     *  DEFLATE level 9) - for bytes that are compressed already (JPEG, PNG, GIF), where deflating costs time and saves nothing. */
    public static WriteArrayBuffer(zip: any, path: string, buffer: ArrayBuffer, Callback:any,
        CreateFolders: boolean = true, Store: boolean = false): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Options: any = { createFolders: CreateFolders };
            if (Store === true) { Options.compression = "STORE"; }
            zip.file(path, buffer, Options);
            ks.DoCallBack(Callback);
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.WriteArrayBuffer"); }
    }

    public static EnsureEmptyFile(zip: any, path: string, cb?: () => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!zip.file(path)) { 
                zip.file(path, ""); 
            }
            if (cb) { cb(); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.EnsureEmptyFile"); }
    }

    // Mutations
    public static Remove(zip: any, path: string, cb?: () => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            zip.remove(path);
            if (cb) { cb(); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.Remove"); }
    }

    public static Move(zip: any, fromPath: string, toPath: string, cb: () => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const f = zip.file(fromPath);
            if (!f) { ks.KGlobalErrorHandler(new Error("Missing file: " + fromPath), "kZip.Move"); return; }
            let readAs: string = "arraybuffer";
            if (kZip.IsTextLike(fromPath)) { readAs = "text"; }
            const p = f.async(readAs);
            p.then(kZip.Move_OnLoaded(zip, fromPath, toPath, cb))
                .catch(kZip.LogError("kZip.Move"));
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.Move"); }
    }

    public static EnsureFolder(zip: any, folderPath: string, cb?: () => void): void {
        if (ks.KErrorMode === true) { return; }

        try {
            zip.folder(folderPath);
            if (cb) { cb(); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kZip.EnsureFolder"); }
    }

    // Handler FACTORIES. Each returns a per-call closure because JSZip's `.then` needs a
    // handler already bound to this call's callback. The returned functions are NAMED, so a
    // stack trace from inside one says which step of which zip operation was running.
    //
    // In error mode each factory hands back the module-level `ZipNoOp` rather than null:
    // `.then(null)` would throw at the call site, and the point of the guard is to go quiet,
    // not to raise a second error while the first is being reported.

    private static FromBlob_OnSuccess(cb: (zip?: any) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function FromBlobLoaded(zip: any) { cb(zip); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.FromBlob_OnSuccess");
            return ZipNoOp;
        }
    }

    private static ToBlob_OnSuccess(cb: (blob?: Blob) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function ToBlobGenerated(b: Blob) { cb(b); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.ToBlob_OnSuccess");
            return ZipNoOp;
        }
    }

    private static ToUint8Array_OnSuccess(cb: (bytes: Uint8Array) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function ToUint8ArrayGenerated(b: Uint8Array) { cb(b); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.ToUint8Array_OnSuccess");
            return ZipNoOp;
        }
    }

    private static ProgressOf(OnProgress: (Percent: number) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function ZipGenerateProgress(Meta: any): void { OnProgress(Meta.percent); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.ProgressOf");
            return ZipNoOp;
        }
    }

    private static ReadText_OnSuccess(cb: (text?: string) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function TextRead(t: string) { cb(t); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.ReadText_OnSuccess");
            return ZipNoOp;
        }
    }

    private static ReadArrayBuffer_OnSuccess(cb: (buffer?: ArrayBuffer) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function ArrayBufferRead(b: ArrayBuffer) { cb(b); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.ReadArrayBuffer_OnSuccess");
            return ZipNoOp;
        }
    }

    private static Move_OnLoaded(zip: any, fromPath: string, toPath: string, cb: () => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function MoveSourceLoaded(data: any) {
                try {
                    zip.file(toPath, data);
                    zip.remove(fromPath);
                    cb();
                } catch (e) { ks.KGlobalErrorHandler(e, "kZip.Move.inner"); }
            };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.Move_OnLoaded");
            return ZipNoOp;
        }
    }

    // Helpers
    private static ListPaths_Collector(prefix: string | undefined, out: string[]) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function CollectPath(relPath: string): void {
                if (!prefix || relPath.startsWith(prefix)) { out.push(relPath); }
            };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.ListPaths_Collector");
            return ZipNoOp;
        }
    }

    private static IsTextLike(path: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            return /\.(xml|txt|json|csv|rels)$/i.test(path);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.IsTextLike");
            return false;
        }
    }

    private static LogError(tag: string) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            return function ZipStepFailed(err: any): void { ks.KGlobalErrorHandler(err, tag); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.LogError");
            return ZipNoOp;
        }
    }

    /**
     * `LogError` when nobody is waiting for the answer, the CALLER'S handler when somebody is.
     *
     * A rejected zip step is two different things depending on who asked. For an internal rewrite
     * it is a programming error and belongs in the central handler. For a file a person just chose
     * it is an ordinary, expected outcome that the caller has a sentence for - and routing it
     * centrally would flip `KErrorMode`, which silences the very screen that was going to explain
     * it. The caller says which by supplying a handler or not.
     */
    private static FailStep(tag: string, OnError?: (err: any) => void) {
        if (ks.KErrorMode === true) { return ZipNoOp; }
        try {
            if (OnError === undefined || OnError === null) { return kZip.LogError(tag); }
            // ERROR-SUPPRESSED-JUSTIFIED: the caller supplied a handler, so the failure is
            // DELEGATED rather than dropped - it is reported by whoever owns the surface, in words
            // that name the file the person chose. Reporting centrally as well would flip
            // KErrorMode and stop that surface rendering at all.
            return function ZipStepDelegated(err: any): void { OnError(err); };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kZip.FailStep");
            return ZipNoOp;
        }
    }
}
